import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSession } from "../App";
import { usePermissions } from "../hooks/usePermissions";
import { apiFetch } from "../lib/apiFetch";

const ADMIN_EMAILS = ["dylan@browning-electric.ca", "cklein@browning-electric.ca"];

const TYPE_OPTIONS = [
  { value: "suggestion", label: "Suggestion" },
  { value: "bug", label: "Bug" },
  { value: "workflow", label: "Workflow" },
  { value: "pricebook", label: "Pricebook" },
  { value: "requests", label: "Requests" },
  { value: "quotes", label: "Quotes" },
  { value: "jai-man", label: "JAI-Man" },
  { value: "other", label: "Other" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "reviewing", label: "Reviewing" },
  { value: "planned", label: "Planned" },
  { value: "done", label: "Done" },
  { value: "archived", label: "Archived" },
];

function fmtDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusTone(status = "") {
  const map = {
    open: { color: "#f59e0b", bg: "rgba(245,158,11,0.14)" },
    reviewing: { color: "#60a5fa", bg: "rgba(96,165,250,0.14)" },
    planned: { color: "#a78bfa", bg: "rgba(167,139,250,0.14)" },
    done: { color: "#34d399", bg: "rgba(52,211,153,0.14)" },
    archived: { color: "#94a3b8", bg: "rgba(148,163,184,0.14)" },
  };
  return map[status] || { color: "#94a3b8", bg: "rgba(148,163,184,0.14)" };
}

function priorityTone(priority = "") {
  const map = {
    low: { color: "#94a3b8", bg: "rgba(148,163,184,0.14)" },
    normal: { color: "#eab308", bg: "rgba(234,179,8,0.14)" },
    high: { color: "#fb923c", bg: "rgba(251,146,60,0.14)" },
    urgent: { color: "#f87171", bg: "rgba(248,113,113,0.14)" },
  };
  return map[priority] || map.normal;
}

function FeedbackCard({ item, isAdmin, onSave }) {
  const [status, setStatus] = useState(item.status || "open");
  const [notes, setNotes] = useState(item.admin_notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(item.status || "open");
    setNotes(item.admin_notes || "");
  }, [item.admin_notes, item.status]);

  const statusStyle = statusTone(item.status);
  const priorityStyle = priorityTone(item.priority);

  return (
    <div className="fb-card">
      <div className="fb-card-top">
        <div>
          <div className="fb-card-title">{item.title}</div>
          <div className="fb-card-meta">
            <span>{item.created_by_name || item.created_by_email}</span>
            <span>{item.page_path || "General"}</span>
            <span>{fmtDate(item.created_at)}</span>
          </div>
        </div>
        <div className="fb-chip-row">
          <span className="fb-chip" style={{ color: priorityStyle.color, background: priorityStyle.bg }}>{item.priority}</span>
          <span className="fb-chip" style={{ color: statusStyle.color, background: statusStyle.bg }}>{item.status}</span>
          <span className="fb-chip">{item.feedback_type}</span>
        </div>
      </div>

      <div className="fb-card-body">{item.details}</div>

      <div className="fb-card-foot">
        <div className="fb-card-hint">
          {item.notification_sent ? "Email alert sent" : item.notification_error ? `Email alert pending: ${item.notification_error}` : "Saved in ElecBid"}
        </div>
      </div>

      {isAdmin && (
        <div className="fb-admin">
          <div className="fb-admin-row">
            <label className="fb-field">
              <span>Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.filter((option) => option.value !== "all").map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <button
              className="fb-btn fb-btn-accent"
              disabled={saving}
              onClick={async () => {
                setSaving(true);
                await onSave(item.id, status, notes);
                setSaving(false);
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
          <label className="fb-field">
            <span>Admin notes</span>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What we decided, next step, or why it was closed"
            />
          </label>
        </div>
      )}
    </div>
  );
}

export default function FeedbackHub() {
  const { session } = useSession() || {};
  const location = useLocation();
  const { impersonating, viewAsEmail } = usePermissions();
  const userEmail = session?.user?.email?.toLowerCase() || "";
  // "View As" swaps the permission context but NOT the session/JWT, so this page
  // used to keep rendering the owner's Owner Inbox (everyone's feedback + admin
  // controls) while previewing a crew member. Force non-admin while impersonating
  // so the preview is faithful. NOTE: real crew are already safe server-side —
  // api/feedback.js listItems scopes non-admins to their own created_by_email.
  const isAdmin = !impersonating && ADMIN_EMAILS.includes(userEmail);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState({
    feedbackType: "suggestion",
    priority: "normal",
    title: "",
    details: "",
    pagePath: location.pathname,
  });

  const loadItems = useCallback(async (nextStatus = statusFilter) => {
    if (!userEmail) return;
    setLoading(true);
    try {
      const res = await apiFetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "listItems",
          userEmail,
          status: nextStatus,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Could not load feedback");
      setItems(data.items || []);
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Could not load feedback" });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, userEmail]);

  useEffect(() => {
    setForm((current) => ({ ...current, pagePath: location.pathname }));
  }, [location.pathname]);

  useEffect(() => {
    loadItems(statusFilter);
  }, [loadItems, statusFilter]);

  // While impersonating, the API still answers with the OWNER's JWT (admin
  // scope = every item), so narrow to the previewed user's own submissions —
  // otherwise "View As Morgan" shows her the whole team's feedback.
  const visibleItems = useMemo(() => (
    impersonating && viewAsEmail
      ? items.filter(i => String(i.created_by_email || "").toLowerCase() === viewAsEmail.toLowerCase())
      : items
  ), [items, impersonating, viewAsEmail]);

  const recentOpenCount = useMemo(
    () => items.filter((item) => item.status === "open" || item.status === "reviewing").length,
    [items]
  );

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await apiFetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "submitItem",
          createdByEmail: userEmail,
          createdByName: session?.user?.user_metadata?.full_name || session?.user?.email || "",
          pagePath: form.pagePath || location.pathname,
          feedbackType: form.feedbackType,
          priority: form.priority,
          title: form.title,
          details: form.details,
          metadata: { source: "feedback_hub" },
        }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Could not submit feedback");
      setForm((current) => ({ ...current, title: "", details: "" }));
      setMessage({
        type: data.emailNotificationSent ? "success" : "warning",
        text: data.emailNotificationSent
          ? "Feedback sent and email alert delivered."
          : `Feedback saved. Email alert not sent yet: ${data.notificationError || "notification unavailable"}`,
      });
      await loadItems(statusFilter);
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Could not submit feedback" });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAdminSave(itemId, status, adminNotes) {
    try {
      const res = await apiFetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "updateItem",
          userEmail,
          itemId,
          status,
          adminNotes,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Could not update feedback");
      setMessage({ type: "success", text: "Feedback item updated." });
      await loadItems(statusFilter);
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Could not update feedback" });
    }
  }

  return (
    <>
      <style>{FEEDBACK_CSS}</style>
      <div className="fb-wrap">
        <div className="fb-hero">
          <div>
            <div className="fb-title">Feedback</div>
            <div className="fb-subtitle">Techs can flag issues, request changes, and leave ideas without needing a separate chat.</div>
          </div>
          <div className="fb-hero-stats">
            <div className="fb-stat"><strong>{visibleItems.length}</strong><span>{isAdmin ? "Inbox Items" : "My Requests"}</span></div>
            <div className="fb-stat"><strong>{recentOpenCount}</strong><span>Still Active</span></div>
          </div>
        </div>

        {message && (
          <div className={`fb-message ${message.type}`}>{message.text}</div>
        )}

        <div className="fb-grid">
          <section className="fb-panel">
            <div className="fb-panel-title">Send Feedback</div>
            <form className="fb-form" onSubmit={handleSubmit}>
              <div className="fb-row">
                <label className="fb-field">
                  <span>Type</span>
                  <select value={form.feedbackType} onChange={(e) => setForm((current) => ({ ...current, feedbackType: e.target.value }))}>
                    {TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
                <label className="fb-field">
                  <span>Priority</span>
                  <select value={form.priority} onChange={(e) => setForm((current) => ({ ...current, priority: e.target.value }))}>
                    {PRIORITY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
              </div>

              <label className="fb-field">
                <span>Page</span>
                <input
                  value={form.pagePath}
                  onChange={(e) => setForm((current) => ({ ...current, pagePath: e.target.value }))}
                  placeholder="/pricelist"
                />
              </label>

              <label className="fb-field">
                <span>Short title</span>
                <input
                  value={form.title}
                  onChange={(e) => setForm((current) => ({ ...current, title: e.target.value }))}
                  placeholder="Example: Price List should show supplier city"
                  required
                />
              </label>

              <label className="fb-field">
                <span>What should change?</span>
                <textarea
                  rows={6}
                  value={form.details}
                  onChange={(e) => setForm((current) => ({ ...current, details: e.target.value }))}
                  placeholder="Say what happened, what you expected, and any material/job/request it relates to."
                  required
                />
              </label>

              <button className="fb-btn fb-btn-accent" type="submit" disabled={submitting}>
                {submitting ? "Sending..." : "Send Feedback"}
              </button>
            </form>
          </section>

          <section className="fb-panel">
            <div className="fb-panel-head">
              <div>
                <div className="fb-panel-title">{isAdmin ? "Owner Inbox" : "My Feedback"}</div>
                <div className="fb-panel-subtitle">{isAdmin ? "Review requests from the team and track what got done." : "You can come back here later to see what was reviewed or changed."}</div>
              </div>
              <label className="fb-filter">
                <span>Status</span>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
            </div>

            {loading ? (
              <div className="fb-empty">Loading feedback...</div>
            ) : visibleItems.length ? (
              <div className="fb-list">
                {visibleItems.map((item) => (
                  <FeedbackCard key={item.id} item={item} isAdmin={isAdmin} onSave={handleAdminSave} />
                ))}
              </div>
            ) : (
              <div className="fb-empty">No feedback items yet for this filter.</div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}

const FEEDBACK_CSS = `
  .fb-wrap{padding:24px 18px 88px;background:#0d0f12;color:#e8eaf0;min-height:100%}
  .fb-hero{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;margin-bottom:18px;flex-wrap:wrap}
  .fb-title{font-size:34px;font-family:'Bebas Neue',sans-serif;letter-spacing:.04em;color:#a5ce3a}
  .fb-subtitle{max-width:760px;color:#8f98a9;line-height:1.55}
  .fb-hero-stats{display:flex;gap:12px;flex-wrap:wrap}
  .fb-stat{background:#161a20;border:1px solid #2a3140;border-radius:14px;padding:14px 16px;min-width:126px;display:flex;flex-direction:column;gap:6px}
  .fb-stat strong{font-size:24px;color:#f8fafc}
  .fb-stat span{font-size:12px;color:#8f98a9;text-transform:uppercase;letter-spacing:.08em}
  .fb-message{border-radius:12px;padding:12px 14px;margin-bottom:16px;border:1px solid transparent}
  .fb-message.success{background:rgba(52,211,153,.12);border-color:rgba(52,211,153,.26);color:#b6f5d7}
  .fb-message.warning{background:rgba(245,158,11,.12);border-color:rgba(245,158,11,.26);color:#fde2a7}
  .fb-message.error{background:rgba(248,113,113,.12);border-color:rgba(248,113,113,.26);color:#fecaca}
  .fb-grid{display:grid;grid-template-columns:minmax(300px,420px) minmax(0,1fr);gap:18px}
  .fb-panel{background:#161a20;border:1px solid #2a3140;border-radius:18px;padding:18px}
  .fb-panel-title{font-size:20px;font-weight:700}
  .fb-panel-subtitle{margin-top:4px;color:#8f98a9;font-size:13px;line-height:1.45}
  .fb-panel-head{display:flex;justify-content:space-between;gap:12px;align-items:flex-end;flex-wrap:wrap;margin-bottom:16px}
  .fb-form{display:flex;flex-direction:column;gap:14px}
  .fb-row{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  .fb-field{display:flex;flex-direction:column;gap:7px}
  .fb-field span,.fb-filter span{font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#8f98a9}
  .fb-field input,.fb-field select,.fb-field textarea,.fb-filter select{background:#0f1318;border:1px solid #2a3140;border-radius:12px;color:#e8eaf0;padding:12px 13px;font:inherit}
  .fb-field textarea{resize:vertical;min-height:120px}
  .fb-btn{border:none;border-radius:12px;padding:12px 16px;font-weight:700;cursor:pointer}
  .fb-btn:disabled{opacity:.6;cursor:default}
  .fb-btn-accent{background:#a5ce3a;color:#111827}
  .fb-list{display:flex;flex-direction:column;gap:14px}
  .fb-card{background:#0f1318;border:1px solid #2a3140;border-radius:16px;padding:15px}
  .fb-card-top{display:flex;justify-content:space-between;gap:12px;align-items:flex-start;flex-wrap:wrap}
  .fb-card-title{font-size:17px;font-weight:700;line-height:1.35}
  .fb-card-meta{display:flex;flex-wrap:wrap;gap:10px;color:#8f98a9;font-size:12px;margin-top:6px}
  .fb-chip-row{display:flex;gap:8px;flex-wrap:wrap}
  .fb-chip{padding:6px 10px;border-radius:999px;background:#1c2430;color:#cbd5e1;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase}
  .fb-card-body{margin-top:14px;white-space:pre-wrap;line-height:1.58;color:#dce1ea}
  .fb-card-foot{margin-top:12px;padding-top:12px;border-top:1px solid #212834}
  .fb-card-hint{font-size:12px;color:#8f98a9}
  .fb-admin{margin-top:14px;padding-top:14px;border-top:1px solid #212834;display:flex;flex-direction:column;gap:12px}
  .fb-admin-row{display:flex;gap:12px;align-items:flex-end}
  .fb-filter{display:flex;flex-direction:column;gap:7px;min-width:140px}
  .fb-empty{padding:26px 12px;color:#8f98a9;text-align:center;border:1px dashed #2a3140;border-radius:14px}
  @media(max-width:980px){.fb-grid{grid-template-columns:1fr}.fb-wrap{padding:18px 14px 88px}}
  @media(max-width:640px){.fb-row{grid-template-columns:1fr}.fb-admin-row{flex-direction:column;align-items:stretch}}
`;
